// Sprint 4B — Tab "Preventivi" nella scheda veicolo.
// Sprint 4B.1 (B1+B2): supporto preventivo embedded con freccia indietro.
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Plus, FileText, Copy, ExternalLink, Calendar as CalendarIcon, AlertTriangle, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { formatEUR } from "@/lib/utils";
import AnagraficaPicker from "@/components/AnagraficaPicker";
import VenditaDetailContent from "@/pages/VenditaDetail";

const STATUS_LABELS = {
  draft: "Bozza", in_corso: "In corso", scaduto: "Scaduto",
  confermato: "Confermato", annullato: "Annullato",
};
const STATUS_BADGE = {
  draft: "bg-slate-100 text-slate-700",
  in_corso: "bg-amber-100 text-amber-800",
  scaduto: "bg-rose-100 text-rose-800",
  confermato: "bg-emerald-100 text-emerald-800",
  annullato: "bg-rose-100 text-rose-800",
};

function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  } catch { return s; }
}

// Sprint CRM-3 — usata anche da «Crea preventivo» della scheda opportunità (crmOpportunitaId)
export function NuovoPreventivoModal({ open, onOpenChange, vehicleId, vehicleCompanyId, onCreated, crmOpportunitaId }) {
  const [clienteId, setClienteId] = useState(null);
  const [coId, setCoId] = useState(null);
  const [giorni, setGiorni] = useState(30);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setClienteId(null); setCoId(null); setGiorni(30);
    }
  }, [open]);

  const submit = async () => {
    // Sprint 4Z A2: cliente_id opzionale (preventivo anonimo)
    if (giorni < 1 || giorni > 30) {
      toast.error("Giorni validità deve essere tra 1 e 30");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/vendite", {
        tipo: "preventivo",
        vehicle_id: vehicleId,
        cliente_id: clienteId || null,
        cointestatario_vendita_id: coId,
        giorni_validita_preventivo: giorni,
        ...(crmOpportunitaId ? { crm_opportunita_id: crmOpportunitaId } : {}),
      });
      const codice = r.data.codice;
      toast.success(clienteId
        ? `Preventivo ${codice} creato`
        : `Preventivo anonimo ${codice} creato (assegna cliente prima della conferma)`);
      onCreated?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione preventivo");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="modal-nuovo-preventivo">
        <DialogHeader>
          <DialogTitle>Nuovo preventivo</DialogTitle>
          <DialogDescription>
            Compila i dati base. Potrai aggiungere righe servizio/accessori dalla scheda preventivo.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Cliente <span className="text-slate-400 text-xs">(opzionale — anonimo se vuoto)</span></Label>
            <AnagraficaPicker
              value={clienteId}
              onChange={setClienteId}
              role="cliente"
              contextCompanyId={vehicleCompanyId || null}
              data-testid="picker-cliente-preventivo"
            />
            {!clienteId && (
              <div className="text-xs text-amber-700 mt-1">
                Preventivo anonimo: dovrai assegnare un cliente prima di confermarlo in vendita.
              </div>
            )}
          </div>
          <div>
            <Label>Cointestatario (opzionale)</Label>
            <AnagraficaPicker
              value={coId}
              onChange={setCoId}
              role="cliente"
              contextCompanyId={vehicleCompanyId || null}
            />
          </div>
          <div>
            <Label>Giorni validità (1-30)</Label>
            <Input
              type="number"
              min={1}
              max={30}
              value={giorni}
              onChange={(e) => setGiorni(Math.max(1, Math.min(30, Number(e.target.value) || 1)))}
              data-testid="input-giorni-validita"
            />
            <div className="text-xs text-slate-500 mt-0.5">
              Data scadenza: {fmtDate(new Date(Date.now() + giorni * 86400000).toISOString().slice(0, 10))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Annulla</Button>
          <Button onClick={submit} disabled={submitting} data-testid="btn-crea-preventivo">
            {submitting ? "Creazione..." : (clienteId ? "Crea preventivo" : "Crea preventivo anonimo")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PreventiviTab({ vehicleId, vehicleStatus, vehicleCompanyId, onGoTo }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const openedPreventivoId = searchParams.get("preventivo_id");
  const [items, setItems] = useState([]);
  const [clientiMap, setClientiMap] = useState({}); // Sprint 4X.4 E2: cache nomi clienti
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const load = async () => {
    if (!vehicleId) return;
    setLoading(true);
    try {
      const r = await api.get(`/vendite?tipo=preventivo&vehicle_id=${vehicleId}&page_size=100`);
      const its = r.data.items || [];
      setItems(its);
      // Sprint 4X.4 E2: prefetch nomi clienti per ogni preventivo
      const cli_ids = [...new Set(its.map((p) => p.cliente_id).filter(Boolean))];
      if (cli_ids.length) {
        const map = {};
        await Promise.all(cli_ids.map(async (cid) => {
          try {
            const ra = await api.get(`/anagrafiche/${cid}`);
            const a = ra.data;
            map[cid] = a.tipo_persona === "azienda"
              ? (a.ragione_sociale || cid.slice(0, 8))
              : ([a.cognome, a.nome].filter(Boolean).join(" ") || cid.slice(0, 8));
          } catch { map[cid] = cid.slice(0, 8); }
        }));
        setClientiMap(map);
      }
    } catch (e) {
      toast.error("Errore caricamento preventivi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vehicleId]);

  const onCreated = (p) => {
    load();
    // Sprint 4B.1 (B1): apri il preventivo in modalità embedded
    setSearchParams((prev) => {
      const np = new URLSearchParams(prev);
      np.set("tab", "preventivi");
      np.set("preventivo_id", p.id);
      return np;
    });
  };

  const openPreventivo = (pid) => {
    setSearchParams((prev) => {
      const np = new URLSearchParams(prev);
      np.set("tab", "preventivi");
      np.set("preventivo_id", pid);
      return np;
    });
  };

  const closePreventivo = () => {
    setSearchParams((prev) => {
      const np = new URLSearchParams(prev);
      np.delete("preventivo_id");
      return np;
    });
    load();
  };

  const isLockedVehicle = ["SOLD", "DELIVERED"].includes(vehicleStatus);

  // Sprint 4B.1 (B1+B2): se è aperto un preventivo, mostra scheda embedded
  if (openedPreventivoId) {
    return (
      <Card className="p-4" data-testid="preventivi-tab-embedded">
        <div className="mb-3 flex items-center gap-2 pb-3 border-b border-slate-200">
          <Button
            size="sm"
            variant="outline"
            onClick={closePreventivo}
            className="border-blue-500 text-blue-700 hover:bg-blue-50 hover:text-blue-800 hover:border-blue-600"
            data-testid="btn-back-to-preventivi-list"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Torna alla lista preventivi
          </Button>
        </div>
        <VenditaDetailContent venditaId={openedPreventivoId} hideHeader={true} onGoTo={onGoTo} />
      </Card>
    );
  }

  return (
    <Card className="p-4" data-testid="preventivi-tab">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-slate-700" />
          <h3 className="font-heading text-lg text-slate-900">Preventivi</h3>
          <span className="text-xs text-slate-400">({items.length})</span>
        </div>
        <Button
          size="sm"
          onClick={() => setModalOpen(true)}
          disabled={isLockedVehicle}
          data-testid="btn-nuovo-preventivo"
        >
          <Plus className="h-4 w-4 mr-1.5" /> Nuovo preventivo
        </Button>
      </div>

      {loading ? (
        <div className="text-center text-slate-500 py-8">Caricamento...</div>
      ) : items.length === 0 ? (
        <div className="text-center text-slate-500 py-8" data-testid="preventivi-empty">
          Nessun preventivo per questo veicolo. Crea il primo per iniziare una trattativa.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((p) => {
            const dataScad = p.data_scadenza_preventivo || p.data_scadenza;
            const giorniRim = dataScad
              ? Math.ceil((new Date(dataScad) - new Date()) / 86400000)
              : null;
            const isWarning = giorniRim !== null && giorniRim <= 3 && giorniRim >= 0
              && p.status === "in_corso";
            return (
              <div
                key={p.id}
                className="border border-slate-200 rounded-md p-3 hover:bg-slate-50 transition-colors"
                data-testid={`preventivo-card-${p.id}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-medium text-slate-900">{p.codice}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_BADGE[p.status] || "bg-slate-100"}`}>
                      {STATUS_LABELS[p.status] || p.status}
                    </span>
                    {isWarning && (
                      <span className="text-[11px] flex items-center gap-1 text-amber-700">
                        <AlertTriangle className="h-3 w-3" /> Scade tra {giorniRim} g.
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openPreventivo(p.id)}
                      data-testid={`btn-apri-${p.id}`}
                    >
                      <ExternalLink className="h-3.5 w-3.5 mr-1" /> Apri
                    </Button>
                    {p.status !== "confermato" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          if (!window.confirm("Duplicare questo preventivo?")) return;
                          try {
                            const r = await api.post(`/vendite/${p.id}/duplica`);
                            toast.success(`Duplicato: ${r.data.codice}`);
                            load();
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || "Errore duplica");
                          }
                        }}
                        data-testid={`btn-duplica-${p.id}`}
                      >
                        <Copy className="h-3.5 w-3.5 mr-1" /> Duplica
                      </Button>
                    )}
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-2 text-sm text-slate-700">
                  <div data-testid={`prev-cliente-${p.id}`}>
                    <span className="text-slate-400 text-xs uppercase tracking-wide">Cliente: </span>
                    {p.cliente_id ? (
                      <span className="font-medium">{clientiMap[p.cliente_id] || p.cliente_id?.slice(0, 8)}</span>
                    ) : (
                      <span className="font-medium text-amber-700 italic">Anonimo</span>
                    )}
                  </div>
                  <div>
                    <CalendarIcon className="h-3 w-3 inline mr-1 text-slate-400" />
                    Scadenza: <span className="font-medium">{fmtDate(dataScad)}</span>
                  </div>
                  {p.totale_vendita_finale != null && (
                    <div data-testid={`prev-totale-${p.id}`}>Totale: <span className="font-mono font-medium">{formatEUR(p.totale_vendita_finale)}</span></div>
                  )}
                  <div className="text-slate-500 text-xs">Creato: {fmtDate(p.created_at)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <NuovoPreventivoModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        vehicleId={vehicleId}
        vehicleCompanyId={vehicleCompanyId}
        onCreated={onCreated}
      />
    </Card>
  );
}
